import type { IModularBenefitDetail } from '@ben/shared/types/columbia';
import type {
  AnySectionForm,
  ConditionalApprovalsForm,
  DocumentsForm,
  EligibilityRulesForm,
  GeneralForm,
  RateTableForm,
  StepsForm,
} from '../elements/types';
import { deepMergeArraysAndObjects, getDirtyValues } from '../utils';
import { sanitizeStepsForSave } from './sanitize-steps';

export interface FormsBundle {
  sectionForms: AnySectionForm[];
  stepsForm: StepsForm;
  rateTableForm: RateTableForm;
  generalForm: GeneralForm;
  conditionalApprovalsForm: ConditionalApprovalsForm;
  documentsForm: DocumentsForm;
  eligibilityRulesForm: EligibilityRulesForm;
}

/**
 * Build a merged benefit payload from base benefit and a bundle of section forms.
 * - Merges only dirty values from each form
 * - Normalizes steps rank
 * - Ensures rate tables and custom modals are explicitly included
 */
export const buildMergedBenefit = (
  benefit: IModularBenefitDetail,
  {
    sectionForms,
    stepsForm,
    rateTableForm,
    generalForm,
    conditionalApprovalsForm,
    documentsForm,
    eligibilityRulesForm,
  }: FormsBundle
): IModularBenefitDetail => {
  const merged = sectionForms.reduce(
    (accumulator: IModularBenefitDetail, form): IModularBenefitDetail => {
      const dirty = getDirtyValues(form) as Partial<IModularBenefitDetail>;
      return deepMergeArraysAndObjects(
        accumulator,
        dirty
      ) as IModularBenefitDetail;
    },
    { ...benefit }
  );

  const currentSteps =
    stepsForm.getValues('additionalConfig.steps') ?? undefined;
  if (currentSteps) {
    merged.additionalConfig = merged.additionalConfig || {};
    merged.additionalConfig.steps = sanitizeStepsForSave(currentSteps);
  }

  const currentRateTables = rateTableForm.getValues(
    'additionalConfig.rateTables'
  );
  if (currentRateTables) {
    merged.additionalConfig = merged.additionalConfig || {};
    merged.additionalConfig.rateTables = currentRateTables;
  }

  const currentCustomModals = generalForm.getValues('customModals');
  if (Array.isArray(currentCustomModals)) {
    merged.customModals = currentCustomModals;
  }

  const currentConditionalApprovals = conditionalApprovalsForm.getValues(
    'conditionalApprovalConfigs'
  );
  if (Array.isArray(currentConditionalApprovals)) {
    merged.conditionalApprovalConfigs = currentConditionalApprovals;
  }

  const currentDocuments = documentsForm.getValues('documents');
  if (Array.isArray(currentDocuments)) {
    merged.documents = currentDocuments;
  }

  const currentEligibilityRules =
    eligibilityRulesForm.getValues('eligibilityRules');
  if (Array.isArray(currentEligibilityRules)) {
    merged.eligibilityRules = currentEligibilityRules;
  }

  return merged;
};

export default buildMergedBenefit;
